import {
  ButtonHTMLAttributes,
  ElementType,
  FC,
  ReactNode,
  forwardRef,
} from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { Download, Plus, RefreshCw, Send, Trash2, Upload } from "lucide-react";
import { PunchupSpinner } from "@punchup/ui-common/punchup/primitives/feedback/PunchupSpinner";
import { cx } from "../../../utils/cx";

export interface PunchupButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "secondary-inverse"
    | "destructive"
    | "outline"
    | "quiet";
  /**
   * Button height/padding/type size. `md` (40px) is the default; `sm` (32px) suits
   * dense rows, `lg` (48px) suits prominent CTAs (e.g. a full-width subscribe action).
   * Drives only geometry — variant/tone is orthogonal.
   */
  size?: "sm" | "md" | "lg";
  /**
   * Stretch the button to fill its container (`width: 100%`). Off by default — a button
   * shrinks to its content. Use for a prominent full-width CTA (e.g. a subscribe action
   * in a card). Orthogonal to `size` / `variant`. For full-width-on-mobile-only, drive
   * it from a responsive container (a column flex parent stretches the button) rather
   * than a breakpoint on the button.
   */
  fullWidth?: boolean;
  /**
   * Tightens horizontal padding to `space-4` (16px). The `primary` / `secondary` variants
   * default to the wide paired-form-footer padding (`--punchup-button-padding-x-wide` = 48px at
   * md); a standalone action button (an "Add" / toolbar CTA that isn't a full-width form footer)
   * should not carry that. Orthogonal to `size` / `variant`.
   */
  compact?: boolean;
  /** Legacy text prop — prefer children for new callsites. */
  text?: string;
  children?: ReactNode;
  /**
   * When true, disables the button and fades its label out under a centered spinner,
   * keeping the button's width. No visual effect with `asChild`.
   */
  isLoading?: boolean;
  /**
   * Optional leading icon override — this is the **left** icon (rendered before the
   * label). A future refactor may rename this to `leftIcon`; keep using `icon` for now.
   * - When provided (any node, including `null`), it replaces the variant's default icon.
   *   Pass `null` to render no leading icon on a `destructive` button.
   * - When omitted, the `destructive` variant keeps its conventional `Trash2` icon and
   *   every other variant renders no icon — preserving every existing call site.
   * Use for danger actions that are not deletions (e.g. seats manual-mode activation).
   */
  icon?: ReactNode;
  /**
   * Optional **right** (trailing) icon, rendered after the label. Use for an
   * affordance that points at the button's result, e.g. a chevron on a dropdown
   * trigger. Like the label, it fades out while `isLoading` (the spinner takes over).
   */
  rightIcon?: ReactNode;
  /**
   * Render as the single child element instead of a `<button>`, keeping every
   * button style. Pass a `next/link` `<Link>` (or `<a>`) as the sole child for a
   * **navigational** action so it stays a real link — middle-click / open-in-new-tab,
   * context menu, and a crawlable `href` — while looking like a button. The
   * `icon` / `rightIcon` and label compose *inside* the slotted element. Do not use
   * for actions that submit or mutate (those stay real `<button>`s).
   */
  asChild?: boolean;
}

const VARIANT_CLASS: Record<
  NonNullable<PunchupButtonProps["variant"]>,
  string
> = {
  primary: "punchupButtonPrimary",
  secondary: "punchupButtonSecondary",
  "secondary-inverse": "punchupButtonSecondaryInverse",
  destructive: "punchupButtonDestructive",
  outline: "punchupButtonOutline",
  quiet: "punchupButtonQuiet",
};

// md is the default height and needs no modifier class.
const SIZE_CLASS: Record<NonNullable<PunchupButtonProps["size"]>, string> = {
  sm: "punchupButtonSm",
  md: "",
  lg: "punchupButtonLg",
};

// forwardRef so the button works as a Radix `asChild` trigger (dropdown / popover /
// tooltip): those clone the child and attach a ref to the underlying DOM node to
// wire up open/close and focus. A plain FC swallows that ref (React warns) and the
// trigger never opens. The ref also reaches the slotted element when `asChild`.
export const PunchupButton = forwardRef<HTMLButtonElement, PunchupButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      fullWidth = false,
      compact = false,
      text,
      children,
      isLoading,
      icon,
      rightIcon,
      asChild = false,
      disabled,
      className = "",
      ...props
    },
    ref
  ) => {
    const variantClass = VARIANT_CLASS[variant];
    const sizeClass = SIZE_CLASS[size];
    // Two sequential decisions: (1) an explicit `icon` (including `null`) always wins,
    // (2) otherwise the destructive variant keeps its conventional Trash2 default and
    // every other variant renders no leading icon.
    let leadingIcon: ReactNode;
    if (icon !== undefined) {
      leadingIcon = icon;
    } else {
      leadingIcon =
        variant === "destructive" ? <Trash2 size={16} aria-hidden /> : null;
    }
    // `asChild` renders the caller's element (e.g. a Link) via Radix Slot instead of
    // a <button>. `Slottable` marks the label as the merge target so the leading /
    // trailing icons compose *inside* that element (e.g. `<a>{icon}Label{chevron}</a>`).
    // These MUST be DIRECT children of Comp — wrapping them in a Fragment hides the
    // Slottable from Slot and the merge silently no-ops.
    const Comp: ElementType = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cx(
          "punchupButton",
          variantClass,
          sizeClass,
          fullWidth && "punchupButtonFullWidth",
          compact && "punchupButtonCompact",
          isLoading && !asChild && "punchupButtonLoading",
          className
        )}
        disabled={disabled || isLoading}
        aria-disabled={disabled || isLoading ? true : undefined}
        aria-busy={(isLoading && !asChild) || undefined}
        {...props}
      >
        {leadingIcon}
        <Slottable>{children ?? text}</Slottable>
        {rightIcon}
        {isLoading && !asChild && (
          <span className="punchupButtonSpinner">
            <PunchupSpinner size={16} variant="current" />
          </span>
        )}
      </Comp>
    );
  }
);

PunchupButton.displayName = "PunchupButton";

/**
 * Add button — orange outline with a leading Plus icon.
 *
 * Use this wherever an action opens a create form or adds a new entity.
 * The Plus icon is always rendered automatically — callers must NOT add it manually.
 */
export const PunchupAddButton: FC<Omit<PunchupButtonProps, "variant">> = (
  props
) => (
  <PunchupButton variant="outline" {...props}>
    <Plus size={16} aria-hidden />
    {props.children ?? props.text}
  </PunchupButton>
);

interface IconLabelButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  /**
   * Required as a string so the wrapper can use it as both the `aria-label`
   * (accessible name for icon-only mode) and the `title` attribute (hover
   * tooltip). Either may be overridden via an explicit `aria-label` / `title`
   * prop. Whether the visible label ever appears as text is governed by the
   * wrapper's modifier class (`punchupButtonIconOnly` hides it at every
   * viewport; `punchupButtonIconCollapse` hides it only below 768px).
   */
  children: string;
  /**
   * When true, the label and icon fade out under a centered 16px
   * `PunchupSpinner` and the button is disabled — the in-progress signal for a refresh / download /
   * upload / send. Forwarded to the base `PunchupButton.isLoading`.
   */
  isLoading?: boolean;
}

/**
 * Resolve the accessible-name attributes for an icon-label button: both
 * `aria-label` and `title` default to the `children` string and may be
 * overridden independently by the caller. Centralising this keeps the
 * override-vs-default contract identical across all four icon-label wrappers.
 */
const resolveAccessibleAttrs = ({
  children,
  "aria-label": ariaLabel,
  title,
}: {
  children: string;
  "aria-label"?: string;
  title?: string;
}): { "aria-label": string; title: string } => ({
  "aria-label": ariaLabel ?? children,
  title: title ?? children,
});

interface IconLabelButtonInternalProps extends IconLabelButtonProps {
  /** Leading icon node — rendered before the (CSS-collapsible) label. */
  icon: ReactNode;
  /** Base visual style passed through to `PunchupButton`. */
  variant: NonNullable<PunchupButtonProps["variant"]>;
  /** Modifier class controlling the icon-only / icon-collapse behaviour. */
  modifierClass: "punchupButtonIconOnly" | "punchupButtonIconCollapse";
}

/**
 * Shared body for the four icon-label wrappers (Refresh, Download, Upload,
 * Send). They differ only by icon, base variant, and modifier class — this
 * helper guarantees identical prop handling (accessible-name resolution,
 * className merge, label span) across all four.
 */
const IconLabelButton: FC<IconLabelButtonInternalProps> = ({
  icon,
  variant,
  modifierClass,
  children,
  className = "",
  title,
  "aria-label": ariaLabel,
  ...props
}) => (
  <PunchupButton
    variant={variant}
    {...resolveAccessibleAttrs({ children, "aria-label": ariaLabel, title })}
    className={cx(modifierClass, className)}
    {...props}
  >
    {icon}
    <span className="punchupButtonLabel">{children}</span>
  </PunchupButton>
);

/**
 * Refresh button — quiet secondary icon-only square with a `RefreshCw` icon.
 *
 * Re-fetch / re-validate data (analytics, table page headers, etc.). Reserved
 * for utility actions that should not compete visually with primary CTAs in the
 * same row. The `RefreshCw` icon is always rendered automatically — callers
 * must NOT add it manually. Pass `disabled` while data is loading to prevent
 * overlapping requests.
 *
 * The `children` string doubles as the button's `aria-label` (accessible name)
 * and `title` attribute (hover tooltip); either may be overridden. See
 * `Punchup/CLAUDE.md → Buttons` for the utility-vs-navigation taxonomy that
 * puts Refresh in the secondary tier.
 */
export const PunchupRefreshButton: FC<IconLabelButtonProps> = (props) => (
  <IconLabelButton
    icon={<RefreshCw size={16} aria-hidden />}
    variant="secondary"
    modifierClass="punchupButtonIconOnly"
    {...props}
  />
);

/**
 * Download button — quiet secondary icon-only square with a `Download` icon.
 *
 * Use for any "download CSV / XLSX / image" action. The button is always
 * rendered as a 40x40 icon-only square — the label never appears as text;
 * `children` is pinned as both the `aria-label` (accessible name) and the
 * `title` attribute (hover tooltip), either of which the caller may override.
 * See `Punchup/CLAUDE.md → Buttons` for the utility-vs-navigation taxonomy that
 * puts Download in the secondary tier.
 *
 * Do not write a custom outline button with a manual `<Download>` icon.
 */
export const PunchupDownloadButton: FC<IconLabelButtonProps> = (props) => (
  <IconLabelButton
    icon={<Download size={16} aria-hidden />}
    variant="secondary"
    modifierClass="punchupButtonIconOnly"
    {...props}
  />
);

/**
 * Upload button — orange outline with a leading `Upload` icon.
 *
 * Mirrors `PunchupDownloadButton`'s mobile-collapse behaviour: below 768px the
 * label hides and the button becomes an icon-only square. Use this for any
 * upload-CSV-style action.
 */
export const PunchupUploadButton: FC<IconLabelButtonProps> = (props) => (
  <IconLabelButton
    icon={<Upload size={16} aria-hidden />}
    variant="outline"
    modifierClass="punchupButtonIconCollapse"
    {...props}
  />
);

/**
 * Send button — orange outline with a leading `Send` icon.
 *
 * Mirrors `PunchupDownloadButton` / `PunchupUploadButton`: collapses to an
 * icon-only square below 768px so the title-row action group stays compact on
 * mobile. Use this for any "navigate to send tickets / messages / etc." action
 * that originates from the table-page title row.
 */
export const PunchupSendButton: FC<IconLabelButtonProps> = (props) => (
  <IconLabelButton
    icon={<Send size={16} aria-hidden />}
    variant="outline"
    modifierClass="punchupButtonIconCollapse"
    {...props}
  />
);
